import { useState, useEffect } from "react";
import axios from "../utils/axios";
import {
  formatChannelOptionLabel,
  sortChannelsByCategoryAndName,
} from "../utils/channelOptions";
import CollapsibleSection from "./CollapsibleSection";
import CustomEmojiPicker from "./EmojiPicker";
import "./ReactionRoles.css";

interface Role {
  id: string;
  name: string;
  color: string;
}

interface Channel {
  id: string;
  name: string;
  type?: number;
  parentId?: string | null;
  categoryName?: string | null;
}

interface Emoji {
  id: string;
  name: string;
  animated: boolean;
  url: string;
}

interface ReactionRoleConfig {
  id: number;
  guild_id: string;
  channel_id: string;
  message_id: string;
  emoji: string;
  role_id: string;
  created_at: string;
}

interface ReactionRolesProps {
  guildId: string;
  channels: Channel[];
  roles: Role[];
  emojis: Emoji[];
}

interface ReactionMapping {
  emoji: string;
  roleId: string;
}

function ReactionRoles({
  guildId,
  channels,
  roles,
  emojis,
}: ReactionRolesProps) {
  const [reactionRoles, setReactionRoles] = useState<ReactionRoleConfig[]>([]);
  const [messageLabels, setMessageLabels] = useState<Record<string, string>>(
    {},
  );
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingContentMessageId, setEditingContentMessageId] = useState<
    string | null
  >(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiPickerTarget, setEmojiPickerTarget] = useState<number | null>(
    null,
  ); // index for create form reactions

  // Form state
  const [selectedChannel, setSelectedChannel] = useState("");
  const [messageTitle, setMessageTitle] = useState("🎭 Reaction Roles");
  const [messageDescription, setMessageDescription] = useState(
    "React to this message to get a role!",
  );
  const [reactions, setReactions] = useState<ReactionMapping[]>([
    { emoji: "", roleId: "" },
  ]);
  const [creating, setCreating] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(
    null,
  );
  const [addingReaction, setAddingReaction] = useState(false);
  const [updatingMessage, setUpdatingMessage] = useState(false);

  // Sort channels and roles alphabetically
  const sortedChannels = sortChannelsByCategoryAndName(channels);
  const sortedRoles = [...(roles || [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  useEffect(() => {
    fetchReactionRoles();
  }, [guildId]);

  const fetchReactionRoles = async () => {
    try {
      const response = await axios.get(`/api/server/${guildId}/reaction-roles`);
      setReactionRoles(response.data.reactionRoles || response.data);
      setMessageLabels(response.data.messageLabels || {});
    } catch (error) {
      console.error("Error fetching reaction roles:", error);
    }
  };

  const addReactionRow = () => {
    setReactions([...reactions, { emoji: "", roleId: "" }]);
  };

  const removeReactionRow = (index: number) => {
    setReactions(reactions.filter((_, i) => i !== index));
  };

  const updateReaction = (
    index: number,
    field: "emoji" | "roleId",
    value: string,
  ) => {
    const updated = [...reactions];
    updated[index][field] = value;
    setReactions(updated);
  };

  const handleCreate = async () => {
    if (!selectedChannel) {
      alert("Please select a channel");
      return;
    }

    const validReactions = reactions
      .map((reaction) => ({
        emoji: reaction.emoji.trim(),
        roleId: reaction.roleId.trim(),
      }))
      .filter((reaction) => reaction.emoji && reaction.roleId);
    if (validReactions.length === 0) {
      alert("Please add at least one reaction-role mapping");
      return;
    }

    setCreating(true);
    try {
      await axios.post(`/api/server/${guildId}/reaction-roles/message`, {
        channelId: selectedChannel,
        title: messageTitle,
        description: messageDescription,
        reactions: validReactions,
        label: messageTitle.trim() || "🎭 Reaction Roles",
      });

      alert("Reaction role message created successfully!");
      setShowCreateForm(false);
      setSelectedChannel("");
      setMessageTitle("🎭 Reaction Roles");
      setMessageDescription("React to this message to get a role!");
      setReactions([{ emoji: "", roleId: "" }]);
      fetchReactionRoles();
    } catch (error: any) {
      console.error("Error creating reaction role:", error);
      alert(
        `Failed to create reaction role message: ${error.response?.data?.error || error.message}`,
      );
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (messageId: string) => {
    if (
      !confirm(
        "Are you sure you want to delete this reaction role message? This will remove the message from Discord.",
      )
    ) {
      return;
    }

    setDeletingMessageId(messageId);
    try {
      await axios.delete(
        `/api/server/${guildId}/reaction-roles/message/${messageId}`,
      );
      alert("Reaction role message deleted successfully!");
      fetchReactionRoles();
    } catch (error: any) {
      console.error("Error deleting reaction role:", error);
      alert(
        `Failed to delete reaction role message: ${error.response?.data?.error || error.message}`,
      );
    } finally {
      setDeletingMessageId(null);
    }
  };

  const handleAddReaction = async (
    messageId: string,
    emoji: string,
    roleId: string,
  ) => {
    const trimmedEmoji = emoji.trim();
    const trimmedRoleId = roleId.trim();

    if (!trimmedEmoji || !trimmedRoleId) {
      alert("Please provide both emoji and role");
      return;
    }

    setAddingReaction(true);
    try {
      await axios.post(
        `/api/server/${guildId}/reaction-roles/message/${messageId}/add`,
        {
          emoji: trimmedEmoji,
          roleId: trimmedRoleId,
        },
      );
      alert("Reaction added successfully!");
      fetchReactionRoles();
    } catch (error: any) {
      console.error("Error adding reaction:", error);
      alert(
        `Failed to add reaction: ${error.response?.data?.error || error.message}`,
      );
    } finally {
      setAddingReaction(false);
    }
  };

  const handleUpdateMessage = async (
    messageId: string,
    title: string,
    description: string,
  ) => {
    if (
      !title ||
      title.trim() === "" ||
      !description ||
      description.trim() === ""
    ) {
      alert("Title and description cannot be empty");
      return;
    }

    setUpdatingMessage(true);
    try {
      await axios.put(
        `/api/server/${guildId}/reaction-roles/message/${messageId}/content`,
        {
          title: title.trim(),
          description: description.trim(),
        },
      );
      alert("Message content updated successfully!");
      setEditingContentMessageId(null);
      fetchReactionRoles();
    } catch (error: any) {
      console.error("Error updating message content:", error);
      alert(
        `Failed to update message content: ${error.response?.data?.error || error.message}`,
      );
    } finally {
      setUpdatingMessage(false);
    }
  };

  const handleDeleteReaction = async (reactionId: number, emoji: string) => {
    if (!confirm(`Are you sure you want to remove the ${emoji} reaction?`)) {
      return;
    }

    try {
      await axios.delete(`/api/server/${guildId}/reaction-roles/${reactionId}`);
      alert("Reaction removed successfully!");
      fetchReactionRoles();
    } catch (error: any) {
      console.error("Error deleting reaction:", error);
      alert(
        `Failed to delete reaction: ${error.response?.data?.error || error.message}`,
      );
    }
  };

  // Group reaction roles by message
  const groupedReactionRoles = reactionRoles.reduce(
    (acc, rr) => {
      if (!acc[rr.message_id]) {
        acc[rr.message_id] = [];
      }
      acc[rr.message_id].push(rr);
      return acc;
    },
    {} as Record<string, ReactionRoleConfig[]>,
  );

  return (
    <div className="reaction-roles-section">
      <CollapsibleSection
        title="🎭 Reaction Roles"
        count={Object.keys(groupedReactionRoles).length}
        defaultCollapsed={true}
      >
        <div className="section-header-actions">
          <button
            className="btn btn-primary no-lift"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? "Cancel" : "+ Create New"}
          </button>
        </div>

        {showCreateForm && (
          <div className="create-form card">
            <h4>Create Reaction Role Message</h4>

            <div className="form-group">
              <label>Channel</label>
              <select
                value={selectedChannel}
                onChange={(e) => setSelectedChannel(e.target.value)}
                className="form-select"
              >
                <option value="">Select a channel...</option>
                {sortedChannels.map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    {formatChannelOptionLabel(channel)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Message Title (also used as label)</label>
              <input
                type="text"
                value={messageTitle}
                onChange={(e) => setMessageTitle(e.target.value)}
                className="form-input"
                placeholder="e.g., Color Roles, Game Roles, Choose your roles!"
              />
            </div>

            <div className="form-group">
              <label>Message Description</label>
              <textarea
                value={messageDescription}
                onChange={(e) => setMessageDescription(e.target.value)}
                className="form-textarea"
                rows={3}
                placeholder="e.g., React with an emoji to get the corresponding role"
              />
            </div>

            <div className="form-group">
              <label>Reaction-Role Mappings</label>
              {reactions.map((reaction, index) => (
                <div key={index} className="reaction-row">
                  <div className="emoji-input-group">
                    <input
                      type="text"
                      value={reaction.emoji}
                      onChange={(e) =>
                        updateReaction(index, "emoji", e.target.value)
                      }
                      className="form-input emoji-input"
                      placeholder="Emoji (e.g., 🎮 or custom emoji ID)"
                    />
                    <button
                      type="button"
                      className="btn btn-secondary btn-emoji"
                      onClick={() => {
                        setEmojiPickerTarget(index);
                        setShowEmojiPicker(true);
                      }}
                      title="Pick emoji"
                    >
                      😀
                    </button>
                  </div>
                  <select
                    value={reaction.roleId}
                    onChange={(e) =>
                      updateReaction(index, "roleId", e.target.value)
                    }
                    className="form-select"
                  >
                    <option value="">Select a role...</option>
                    {sortedRoles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                  {reactions.length > 1 && (
                    <button
                      className="btn btn-danger btn-small"
                      onClick={() => removeReactionRow(index)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                className="btn btn-secondary btn-small"
                onClick={addReactionRow}
              >
                + Add Another Reaction
              </button>
            </div>

            <div className="form-actions">
              <button
                className="btn btn-primary no-lift"
                onClick={handleCreate}
                disabled={creating}
              >
                {creating ? "Creating..." : "Create Message"}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowCreateForm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="reaction-roles-list">
          {Object.keys(groupedReactionRoles).length === 0 ? (
            <p className="empty-message">
              No reaction roles configured. Click "Create New" to get started!
            </p>
          ) : (
            <div className="reaction-roles-table-container">
              <table className="reaction-roles-table">
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th>Title</th>
                    <th>Reactions</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedReactionRoles).map(
                    ([messageId, configs]) => {
                      const firstConfig = configs[0];
                      const channel = channels.find(
                        (c) => c.id === firstConfig.channel_id,
                      );
                      const label =
                        messageLabels[messageId] || "Reaction Roles";
                      const truncatedTitle =
                        label.length > 70
                          ? label.substring(0, 67) + "..."
                          : label;

                      return (
                        <tr key={messageId}>
                          <td className="channel-cell">
                            <span className="channel-name">
                              {formatChannelOptionLabel(channel)}
                            </span>
                          </td>
                          <td className="title-cell">
                            <span className="title-text" title={label}>
                              {truncatedTitle}
                            </span>
                          </td>
                          <td className="reactions-cell">
                            <div className="reaction-badges">
                              {configs.map((config) => (
                                <span
                                  key={config.id}
                                  className="reaction-badge"
                                  title={
                                    roles.find((r) => r.id === config.role_id)
                                      ?.name
                                  }
                                >
                                  {renderEmoji(config.emoji)}
                                </span>
                              ))}
                              <span className="reaction-count">
                                {configs.length}
                              </span>
                            </div>
                          </td>
                          <td className="actions-cell">
                            <button
                              className="btn btn-primary btn-small"
                              onClick={() =>
                                setEditingContentMessageId(messageId)
                              }
                            >
                              ✏️ Edit
                            </button>
                            <button
                              className="btn btn-danger btn-small"
                              onClick={() => handleDelete(messageId)}
                              disabled={deletingMessageId === messageId}
                            >
                              {deletingMessageId === messageId ? (
                                <>
                                  <span className="spinner-small"></span>
                                  Deleting...
                                </>
                              ) : (
                                "🗑️"
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    },
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Reaction Role Editor Modal */}
        {editingContentMessageId &&
          groupedReactionRoles[editingContentMessageId]?.length > 0 && (
            <ReactionRoleEditorModal
              messageId={editingContentMessageId}
              guildId={guildId}
              reactionRoles={groupedReactionRoles[editingContentMessageId]}
              channel={channels.find(
                (c) =>
                  c.id ===
                  groupedReactionRoles[editingContentMessageId][0].channel_id,
              )}
              roles={sortedRoles}
              emojis={emojis}
              onClose={() => setEditingContentMessageId(null)}
              onSave={fetchReactionRoles}
              onUpdateMessage={handleUpdateMessage}
              onAddReaction={handleAddReaction}
              onDeleteReaction={handleDeleteReaction}
              isUpdating={updatingMessage || addingReaction}
            />
          )}
      </CollapsibleSection>

      {showEmojiPicker && (
        <CustomEmojiPicker
          emojis={emojis}
          onSelectEmoji={(emoji) => {
            if (emojiPickerTarget !== null) {
              updateReaction(emojiPickerTarget, "emoji", emoji);
            }
            setShowEmojiPicker(false);
            setEmojiPickerTarget(null);
          }}
          onClose={() => {
            setShowEmojiPicker(false);
            setEmojiPickerTarget(null);
          }}
        />
      )}
    </div>
  );
}

// Helper function to render emoji (custom Discord emoji or unicode)
function renderEmoji(emoji: string) {
  // Check if it's a custom Discord emoji: <:name:id> or <a:name:id>
  const customEmojiMatch = emoji.match(/<(a?):(\w+):(\d+)>/);

  if (customEmojiMatch) {
    const [, animated, name, id] = customEmojiMatch;
    const extension = animated ? "gif" : "png";
    const emojiUrl = `https://cdn.discordapp.com/emojis/${id}.${extension}`;

    return (
      <img
        src={emojiUrl}
        alt={name}
        className="custom-emoji-image"
        title={`:${name}:`}
      />
    );
  }

  // It's a unicode emoji, return as-is
  return <span>{emoji}</span>;
}

// Reaction Role Editor Modal Component
interface ReactionRoleEditorModalProps {
  messageId: string;
  guildId: string;
  reactionRoles: ReactionRoleConfig[];
  channel?: Channel;
  roles: Role[];
  emojis: Emoji[];
  onClose: () => void;
  onSave: () => void;
  onUpdateMessage: (
    messageId: string,
    title: string,
    description: string,
  ) => Promise<void>;
  onAddReaction: (
    messageId: string,
    emoji: string,
    roleId: string,
  ) => Promise<void>;
  onDeleteReaction: (reactionId: number, emoji: string) => Promise<void>;
  isUpdating: boolean;
}

function ReactionRoleEditorModal({
  messageId,
  guildId,
  reactionRoles,
  channel,
  roles,
  emojis,
  onClose,
  onSave,
  onUpdateMessage,
  onAddReaction,
  onDeleteReaction,
  isUpdating,
}: ReactionRoleEditorModalProps) {
  const [title, setTitle] = useState("🎭 Reaction Roles");
  const [description, setDescription] = useState(
    "React to this message to get a role!",
  );
  const [newEmoji, setNewEmoji] = useState("");
  const [newRoleId, setNewRoleId] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [activeTab, setActiveTab] = useState<"reactions" | "message">(
    "reactions",
  );

  useEffect(() => {
    let cancelled = false;
    const fetchMessageContent = async () => {
      try {
        const response = await axios.get(
          `/api/server/${guildId}/reaction-roles/message/${messageId}/content`,
        );
        if (cancelled) {
          return;
        }
        const data = response.data || {};
        setTitle(data.title || "🎭 Reaction Roles");
        setDescription(data.description || "React to this message to get a role!");
      } catch (error) {
        if (!cancelled) {
          console.error("Error loading message content:", error);
        }
      }
    };

    fetchMessageContent();
    return () => {
      cancelled = true;
    };
  }, [guildId, messageId]);

  const handleSaveMessage = async () => {
    await onUpdateMessage(messageId, title, description);
  };

  const handleAddReaction = async () => {
    const trimmedEmoji = newEmoji.trim();
    const trimmedRoleId = newRoleId.trim();

    if (trimmedEmoji && trimmedRoleId) {
      await onAddReaction(messageId, trimmedEmoji, trimmedRoleId);
      setNewEmoji("");
      setNewRoleId("");
      onSave();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container reaction-role-editor"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Edit Reaction Role</h3>
          <button className="modal-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-info">
          <span className="info-label">Channel:</span>
          <span className="info-value">
            {channel ? formatChannelOptionLabel(channel) : "Unknown channel"}
          </span>
          <span className="info-label">Message ID:</span>
          <span className="info-value">{messageId}</span>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab-btn ${activeTab === "reactions" ? "active" : ""}`}
            onClick={() => setActiveTab("reactions")}
          >
            Reactions ({reactionRoles.length})
          </button>
          <button
            className={`tab-btn ${activeTab === "message" ? "active" : ""}`}
            onClick={() => setActiveTab("message")}
          >
            Message Content
          </button>
        </div>

        <div className="modal-content">
          {activeTab === "reactions" && (
            <div className="tab-content">
              <h4>Current Reactions</h4>
              <div className="reaction-list-editor">
                {reactionRoles.map((config) => {
                  const role = roles.find((r) => r.id === config.role_id);
                  return (
                    <div key={config.id} className="reaction-item-editor">
                      <span className="emoji-large">
                        {renderEmoji(config.emoji)}
                      </span>
                      <span className="arrow">→</span>
                      <span
                        className="role-badge"
                        style={{ backgroundColor: role?.color || "#99AAB5" }}
                      >
                        {role?.name || "Unknown Role"}
                      </span>
                      <button
                        className="btn-remove"
                        onClick={() =>
                          onDeleteReaction(config.id!, config.emoji)
                        }
                        title="Remove reaction"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="add-reaction-section">
                <h4>Add New Reaction</h4>
                <div className="add-reaction-row">
                  <div className="emoji-input-group">
                    <input
                      type="text"
                      value={newEmoji}
                      onChange={(e) => setNewEmoji(e.target.value)}
                      className="form-input"
                      placeholder="Emoji"
                    />
                    <button
                      className="btn btn-secondary btn-small"
                      onClick={() => setShowEmojiPicker(true)}
                    >
                      😀
                    </button>
                  </div>
                  <select
                    value={newRoleId}
                    onChange={(e) => setNewRoleId(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select a role...</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-primary btn-small"
                    onClick={handleAddReaction}
                    disabled={!newEmoji || !newRoleId || isUpdating}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === "message" && (
            <div className="tab-content">
              <h4>Edit Message Content</h4>
              <div className="form-group">
                <label>Message Title (also used as label)</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="form-input"
                  placeholder="Enter message title..."
                />
              </div>
              <div className="form-group">
                <label>Message Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="form-textarea"
                  placeholder="Enter message description..."
                  rows={4}
                />
              </div>
              <button
                className="btn btn-primary"
                onClick={handleSaveMessage}
                disabled={!title.trim() || !description.trim() || isUpdating}
              >
                {isUpdating ? "Saving..." : "Save Message Content"}
              </button>
            </div>
          )}

        </div>

        {showEmojiPicker && (
          <CustomEmojiPicker
            emojis={emojis}
            onSelectEmoji={(emoji) => {
              setNewEmoji(emoji);
              setShowEmojiPicker(false);
            }}
            onClose={() => setShowEmojiPicker(false)}
          />
        )}
      </div>
    </div>
  );
}

export default ReactionRoles;
